import React, { useState } from 'react';
import DialogWizard from 'src/components/common/DialogWizard.redesign';
import DialogList from 'src/dialogs';
import { DialogWizardPage } from 'src/components/common/DialogWizard.redesign';
import { HorizonInterval, Formation, HorizonPart } from 'src/models/Horizon';
import HorizonSelector from '../../TractComponents/AddHorizonComponents/HorizonSelector';
import { AddHorizonWizardModel } from 'src/models/AddHorizonWizard';
import HorizonIntervalComponent from '../../TractComponents/AddHorizonComponents/HorizonIntervalComponent';
import HorizonSequencingComponent from '../../TractComponents/AddHorizonComponents/HorizonSequencingComponent';
import Horizon from 'src/models/tract_detail/Horizon';
import PropertyUtil from 'src/util/PropertyUtil';
import TractDetail from 'src/models/tract_detail/TractDetail';
import CreateHorizonDto from 'src/models/tract_detail/CreateHorizonDto';
import Form from 'src/components/controls/forms/Form';
import { AnyObject } from 'final-form';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CloneHorizon from 'src/models/CloneHorizon';
import { OwnershipPanel } from 'src/components/dialogs/CloneDataDialog';

interface IAddHorizonDialogProps {
    horizons: Array<Horizon>;
    cloneHorizonData: CloneHorizon;
    uniqueHorizonIntervals: Array<HorizonInterval>;
    currentTract: TractDetail;
    currentClientNumber: number;
    currentProjectNumber: number;
    formations: Array<Formation>;
    idVerificationMessage: string;
    idVerificationResult: boolean;
    isFromChild?: boolean;

    onCloseDialog?: () => void;
    onCreateHorizon: (horizon: CreateHorizonDto) => void;
    onCreateFormation: (formation: Formation) => void;
    onGetData: (data: CloneHorizon) => void;
    setVerificationMessage: (message: string) => void;
    verifyId: (context: string, id: string) => void;
}

const AddHorizonDialog: React.FC<IAddHorizonDialogProps> = (props) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [wizardModel, setWizardModel] = useState(new AddHorizonWizardModel());

    const closeDialog = () => {
        setTimeout(() => {
            setCurrentStep(0);
            setWizardModel(new AddHorizonWizardModel());
        }, 500);
        if (props.onCloseDialog) {
            props.onCloseDialog();
        }
    };

    const horizonSelectorActions = {
        createNewHorizon: () => {
            const model = new AddHorizonWizardModel();
            model.addHorizon = new HorizonInterval();
            model.addHorizon.from = new HorizonPart();
            model.addHorizon.from.isSurface = true;
            model.addHorizon.from.type = 'extent';
            model.addHorizon.to = new HorizonPart();
            model.addHorizon.to.isCenterEarth = true;
            model.addHorizon.to.type = 'extent';
            setWizardModel(JSON.parse(JSON.stringify(model, null, 2)));
            setCurrentStep(1);
        },
        onSelect: (horizonInterval: HorizonInterval) => {
            wizardModel.addHorizon = horizonInterval;
            setWizardModel(wizardModel);
            setCurrentStep(1);
        },
    };

    const horizonIntervalActions = {
        onNext: (horizonInterval: HorizonInterval) => {
            const model = new AddHorizonWizardModel();
            model.addHorizon = horizonInterval;
            setWizardModel(model);
            setCurrentStep(2);
        },
        onPrev: () => {
            setCurrentStep(0);
        },
    };

    const horizonIntervals = () => {
        let cloneHorizonIntervals: Array<HorizonInterval> = [];
        if (PropertyUtil.SafeGet(() => props.currentTract.horizons)) {
            cloneHorizonIntervals = props.currentTract.horizons.map((hor: Horizon) => HorizonInterval.from(hor.horizonInterval));
        }

        const hi = HorizonInterval.from(wizardModel.addHorizon);
        hi.sequencePosition = cloneHorizonIntervals.length + 1;
        hi.id = 0;
        return [...cloneHorizonIntervals, hi];
    };

    const horizonSequencingActions = {
        onNext: (orderedIntervals: Array<HorizonInterval>) => {
            const intervals = orderedIntervals.map((interval: HorizonInterval, index: number) => {
                const ival = HorizonInterval.from(interval);
                ival.sequencePosition = index + 1;
                return ival;
            });

            wizardModel.sequencing = intervals;
            const model = CreateHorizonDto.from(wizardModel);
            model.horizonInterval.from.type = undefined;
            model.horizonInterval.to.type = undefined;
            // props.onCreateHorizon(model);
            // setTimeout(() => setCurrentStep(0), 250);
            setWizardModel(wizardModel);
            setCurrentStep(3);
        },
        onPrev: () => {
            setCurrentStep(1);
        },
    };

    const cloneDataActions = {
        onNext: (
            cloneId: string,
            copyData: boolean,
            copyOwnership: boolean,
            copyLeasehold: boolean,
            copyWorkingInterest: boolean,
            copyOverridingRoyaltyInterest: boolean,
            isChildTract: boolean,
        ) => {
            const model = CreateHorizonDto.from(wizardModel);
            model.horizonInterval.from.type = undefined;
            model.horizonInterval.to.type = undefined;
            model.copyData = copyData;
            if (model.copyData) {
                model.copyHorizonId = cloneId;
                model.copyOwnership = copyOwnership;
                model.copyLeasehold = copyLeasehold;
                model.copyWorkingInterest = copyWorkingInterest;
                model.copyOverridingRoyaltyInterest = copyOverridingRoyaltyInterest;
                model.isChildTract = isChildTract;
                model.tractId = isChildTract === true ? props.currentTract.id : null;
            }

            props.onCreateHorizon(model);
            closeDialog();
            setTimeout(() => setCurrentStep(0), 250);
        },
        onPrev: () => {
            setCurrentStep(2);
        },
    };

    const addFormationActions = {
        onSubmit: (value: AnyObject) => {
            const formation = new Formation();
            formation.name = value.name;
            props.onCreateFormation(formation);
            setCurrentStep(1);
        },
    };

    return (
        <DialogWizard
            title="Add Horizon"
            dialogName={DialogList.ADD_HORIZON}
            showProgress={true}
            currentStep={currentStep}
            onChangeStep={setCurrentStep}
        >
            <DialogWizardPage title="Horizon Search">
                <HorizonSelector
                    horizonIntervals={props.uniqueHorizonIntervals}
                    onSelect={horizonSelectorActions.onSelect}
                    onCreateNew={horizonSelectorActions.createNewHorizon}
                    onCancel={closeDialog}
                />
            </DialogWizardPage>
            <DialogWizardPage title="Horizon Details">
                <HorizonIntervalComponent
                    isInDialog={true}
                    formations={props.formations}
                    showAddFormationPanel={() => console.log('show formation')}
                    horizonInterval={wizardModel.addHorizon}
                    onCancel={closeDialog}
                    onNext={horizonIntervalActions.onNext}
                    onPrev={horizonIntervalActions.onPrev}
                    onGoToFormationPanel={() => setCurrentStep(4)}
                />
            </DialogWizardPage>
            <DialogWizardPage title="Sequencing">
                <HorizonSequencingComponent
                    isInDialog={true}
                    horizons={horizonIntervals()}
                    onCancel={closeDialog}
                    onNext={horizonSequencingActions.onNext}
                    onPrev={horizonSequencingActions.onPrev}
                    customDetailFormat={(item) => <div>{item.toString()}</div>}
                />
            </DialogWizardPage>
            <DialogWizardPage title="Copy Data">
                <OwnershipPanel
                    isInCreateHorizonDialog={true}
                    isFromChild={props.isFromChild}
                    horizons={props.horizons}
                    onNext={cloneDataActions.onNext}
                    onPrev={cloneDataActions.onPrev}
                    contextParams={{ clientNumber: props.currentClientNumber, projectNumber: props.currentProjectNumber }}
                    cloneData={undefined}
                    onCancel={closeDialog}
                />
            </DialogWizardPage>
            <DialogWizardPage title="Add Formation" excludeFromProgress={true}>
                <>
                    <h4>Add Formation to Project</h4>
                    <Form onSubmit={addFormationActions.onSubmit}>
                        <FormTextBox name="name" label="Formation Name" />
                        <div style={{ display: 'inline-block', textAlign: 'end', position: 'relative', right: '0' }}>
                            <Button type="submit" text="Add Formation" intent="primary" />
                            <span style={{ paddingRight: '1rem' }} />
                            <Button intent="primary" text="Cancel" onClick={() => setCurrentStep(1)} />
                        </div>
                    </Form>
                </>
            </DialogWizardPage>
        </DialogWizard>
    );
};

export default AddHorizonDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/CreateHorizonDialog.tsx